import type { TFunction } from 'i18next';
import type { AutoWithdrawalDrafts, AutoWithdrawalRuleDraft } from '@entities/withdrawal';
import { getEnabledDirectDrafts } from '@entities/withdrawal';
import { validateCryptoAddress, type CryptoAddressValidationErrorCode } from '@shared/lib/validate-crypto-address';
import { describeAddressFeedback } from './address-feedback';
import {
  isDecimalGreaterThan,
  isDecimalLessThan,
  parseNonNegativeDecimal,
  parsePositiveDecimal,
} from '@shared/lib/parse-decimal';
import type { AddressValidationErrorCode } from '@shared/lib/validate-usdt-address';

export interface WithdrawalFormValues {
  network: string;
  address: string;
  amount: string;
}

export interface AutoWithdrawalFormValues extends WithdrawalFormValues {
  code: string;
}

export interface AutoWithdrawalRuleFieldErrors {
  destinationAddress?: string;
  thresholdAmount?: string;
  reserveAmount?: string;
  amountMode?: string;
  networkCode?: string;
}

export interface AutoWithdrawalValidationResult {
  formError?: string;
  ruleErrors: Record<string, AutoWithdrawalRuleFieldErrors>;
  code?: string;
}

export const getAddressErrorMessage = (
  errorCode: AddressValidationErrorCode | CryptoAddressValidationErrorCode | undefined,
  t: TFunction,
): string => {
  switch (errorCode) {
    case 'INVALID_ADDRESS_FORMAT':
      return t('withdrawal.validation.invalidAddressFormat');
    case 'INVALID_ADDRESS_CHECKSUM':
      return t('withdrawal.validation.invalidAddressChecksum');
    case 'NETWORK_MISMATCH':
      return t('withdrawal.validation.networkMismatch');
    default:
      return t('withdrawal.validation.invalidAddressFormat');
  }
};

export const validateWithdrawalForm = (
  values: WithdrawalFormValues,
  balance: number,
  t: TFunction,
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!values.network?.trim()) {
    errors.network = t('withdrawal.validation.required');
  }

  if (!values.address?.trim()) {
    errors.address = t('withdrawal.validation.required');
  }

  if (!values.amount?.trim()) {
    errors.amount = t('withdrawal.validation.required');
  } else {
    const amount = parsePositiveDecimal(values.amount);
    if (!amount.valid) {
      errors.amount = t('withdrawal.validation.invalidAmount');
    } else if (isDecimalGreaterThan(amount.normalized, String(balance))) {
      errors.amount = t('withdrawal.validation.exceedsBalance');
    }
  }

  return errors;
};

export const validateAutoWithdrawalCode = (code: string, t: TFunction): string | undefined => {
  if (!code?.trim()) return t('withdrawal.validation.required');
  if (!/^\d{6}$/.test(code)) return t('withdrawal.validation.invalidCode');
  return undefined;
};

const validateRuleDraft = async (
  draft: AutoWithdrawalRuleDraft,
  t: TFunction,
): Promise<AutoWithdrawalRuleFieldErrors> => {
  const errors: AutoWithdrawalRuleFieldErrors = {};

  if (!draft.networkCode) {
    errors.networkCode = t('withdrawal.validation.required');
  }

  if (!draft.destinationAddress?.trim()) {
    errors.destinationAddress = t('withdrawal.validation.required');
  } else {
    const addressResult = await validateCryptoAddress(draft.destinationAddress, draft.networkCode);
    if (!addressResult.isValid && addressResult.errorCode !== 'REQUIRED') {
      errors.destinationAddress = describeAddressFeedback(
        draft.destinationAddress,
        draft.networkCode,
        addressResult.errorCode,
        t,
      ).message;
    }
  }

  const threshold = parsePositiveDecimal(draft.thresholdAmount);
  if (!threshold.valid) {
    errors.thresholdAmount = t('withdrawal.auto.validation.invalidThreshold');
  }

  if (!draft.amountMode) {
    errors.amountMode = t('withdrawal.validation.required');
  }

  if (draft.amountMode === 'KEEP_RESERVE') {
    const reserve = parseNonNegativeDecimal(draft.reserveAmount);
    if (!reserve.valid) {
      errors.reserveAmount = t('withdrawal.auto.validation.invalidReserve');
    } else if (threshold.valid && !isDecimalLessThan(reserve.normalized, threshold.normalized)) {
      errors.reserveAmount = t('withdrawal.auto.validation.reserveMustBeLessThanThreshold');
    }
  }

  return errors;
};

export const validateAutoWithdrawalDrafts = async (
  drafts: AutoWithdrawalDrafts,
  code: string,
  t: TFunction,
): Promise<AutoWithdrawalValidationResult> => {
  const ruleErrors: Record<string, AutoWithdrawalRuleFieldErrors> = {};
  const codeError = code ? validateAutoWithdrawalCode(code, t) : undefined;

  const rulesToValidate =
    drafts.routingMode === 'SINGLE_TARGET_SWAP'
      ? [drafts.singleDraft]
      : getEnabledDirectDrafts(drafts.directDrafts);

  if (drafts.routingMode === 'DIRECT_BY_NETWORK' && rulesToValidate.length === 0) {
    return {
      formError: t('withdrawal.auto.validation.directRequiresEnabledRule'),
      ruleErrors,
      code: codeError,
    };
  }

  await Promise.all(
    rulesToValidate.map(async (draft) => {
      const errors = await validateRuleDraft(draft, t);
      if (Object.keys(errors).length > 0) {
        ruleErrors[draft.key] = errors;
      }
    }),
  );

  return { ruleErrors, code: codeError };
};

/** @deprecated Legacy single-field form validation */
export const validateAutoWithdrawalForm = (
  values: AutoWithdrawalFormValues,
  t: TFunction,
): Record<string, string> => {
  const errors = validateWithdrawalForm(values, Number.MAX_SAFE_INTEGER, t);

  const codeError = validateAutoWithdrawalCode(values.code, t);
  if (codeError) errors.code = codeError;

  return errors;
};

export const getFirstInvalidRuleKey = (
  ruleErrors: Record<string, AutoWithdrawalRuleFieldErrors>,
): string | undefined => Object.keys(ruleErrors)[0];
